// Sequentialized situation step with sub-steps and animations
import { useOnboardingStore } from "@/stores/onboardingStore";
import { TriggerStep } from "./TriggerStep";
import { StakesStep, type StakesData } from "./StakesStep";
import { PreviousAttemptsStep } from "./PreviousAttemptsStep";
import { PriorityStep } from "./PriorityStep";
import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";

interface StepSituationProps {
  workspaceId: string;
  role?: string;
  title?: string;
  decisionType?: string;
  decisionSubtype?: string;
  onComplete?: () => void;
}

export const StepSituation = ({
  workspaceId,
  role,
  title,
  decisionType,
  decisionSubtype,
  onComplete,
}: StepSituationProps) => {
  const store = useOnboardingStore();
  const {
    currentSubStep,
    setSubStep,
    trigger,
    optionA,
    optionB,
    setStructuredTrigger,
    setInitialPreference,
    initialPreference,
    stakes,
    stakesWrongChoiceA,
    stakesWrongChoiceB,
    reversibilityA,
    reversibilityB,
    stakesTimeframeA,
    stakesTimeframeB,
    stakesInaction,
    stakesWorstCase,
    stakesWorstCaseReason,
    stakesWorstCaseTrigger,
    stakesWorstCaseProbability,
    stakesInactionTimeframe,
    setStructuredStakes,
    previousAttempts,
    setPreviousAttempts,
    impactScore,
    urgencyScore,
    impactMetric,
    urgencyReason,
    setScores,
    setSituation,
  } = store;

  const [direction, setDirection] = useState<'forward' | 'backward'>('forward');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentSubStep]);

  const handleTriggerNext = (a: string, b: string, pref: 'a' | 'b' | 'undecided') => {
    setDirection('forward');
    setStructuredTrigger(a, b);
    setInitialPreference(pref);
    setSubStep(1);
  };

  const handleStakesNext = (data: StakesData) => {
    setDirection('forward');
    setStructuredStakes(data);
    setSubStep(2);
  };

  const handlePreviousAttemptsNext = (value: string) => {
    setDirection('forward');
    setPreviousAttempts(value);
    setSubStep(3);
  };

  // Guard: clear previousAttempts if it's identical to trigger
  useEffect(() => {
    if (currentSubStep === 2 && previousAttempts && trigger && previousAttempts === trigger) {
      setPreviousAttempts('');
    }
  }, [currentSubStep, previousAttempts, trigger, setPreviousAttempts]);

  const handlePreviousAttemptsSkip = () => {
    setDirection('forward');
    setPreviousAttempts('');
    setSubStep(3);
  };

  const handlePriorityNext = (impact: number, urgency: number, metric: string, reason: string, extras: {
    timeHorizonDays: number | null;
    riskToleranceLevel: 'conservative' | 'moderate' | 'aggressive' | null;
    successDefinition: string;
    primaryMetric: string;
  }) => {
    setScores(impact, urgency, metric, reason);
    store.setTimeHorizonDays(extras.timeHorizonDays);
    store.setRiskToleranceLevel(extras.riskToleranceLevel);
    store.setSuccessDefinition(extras.successDefinition);
    store.setPrimaryMetric(extras.primaryMetric);
    setSituation(trigger, stakes, previousAttempts);
    onComplete?.();
  };

  const handleBack = () => {
    setDirection('backward');
    if (currentSubStep === 0) return;
    setSubStep(Math.max(0, currentSubStep - 1));
  };

  const getAnimationClass = () => {
    return direction === 'forward' 
      ? 'animate-slide-in-from-right' 
      : 'animate-slide-in-from-left';
  };

  const renderCurrentStep = () => {
    if (currentSubStep === 0) {
      return (
        <TriggerStep
          initialOptionA={optionA}
          initialOptionB={optionB}
          initialPreference={initialPreference}
          onNext={handleTriggerNext}
          onBack={handleBack}
        />
      );
    }

    if (currentSubStep === 1) {
      return (
        <StakesStep
          optionALabel={optionA || "Option A"}
          optionBLabel={optionB || "Option B"}
          initialWrongChoiceA={stakesWrongChoiceA}
          initialWrongChoiceB={stakesWrongChoiceB}
          initialReversibilityA={reversibilityA}
          initialReversibilityB={reversibilityB}
          initialInaction={stakesInaction}
          initialWorstCase={stakesWorstCase}
          initialWorstCaseReason={stakesWorstCaseReason}
          initialWorstCaseTrigger={stakesWorstCaseTrigger}
          initialWorstCaseProbability={stakesWorstCaseProbability}
          initialTimeframeA={stakesTimeframeA}
          initialTimeframeB={stakesTimeframeB}
          initialInactionTimeframe={stakesInactionTimeframe}
          onNext={handleStakesNext}
          onBack={handleBack}
        />
      );
    }

    if (currentSubStep === 2) {
      return (
        <PreviousAttemptsStep
          initialValue={previousAttempts}
          trigger={trigger}
          onNext={handlePreviousAttemptsNext}
          onSkip={handlePreviousAttemptsSkip}
          onBack={handleBack}
        />
      );
    }

    if (currentSubStep === 3) {
      return (
        <PriorityStep
          initialImpact={impactScore}
          initialUrgency={urgencyScore}
          initialImpactMetric={impactMetric}
          initialUrgencyReason={urgencyReason}
          initialTimeHorizonDays={store.timeHorizonDays}
          initialRiskToleranceLevel={store.riskToleranceLevel}
          initialSuccessDefinition={store.successDefinition}
          initialPrimaryMetric={store.primaryMetric}
          onNext={handlePriorityNext}
          onBack={handleBack}
        />
      );
    }

    return null;
  };

  return (
    <div className="space-y-6">
      <div 
        key={currentSubStep}
        className={cn(
          "overflow-hidden",
          getAnimationClass()
        )}
      >
        {renderCurrentStep()}
      </div>
    </div>
  );
};
